import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import colores from "../shared/constant/styles";
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  styled,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Title from "../commons/header_title";
import ConfirmacionPopup from "../components/confirmacion_popup";
import { ventasService } from "../services";
import { VentaResponse } from "../types/api.types";
import { tokenHelper } from "../utils/tokenHelper";
import LockIcon from "@mui/icons-material/Lock";
import bannerFondo from "../assets/images/Banner_fondo.webp";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export default function CargarComprobante() {
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const [email, setEmail] = useState<string | null>(null);
  const [nroPedido, setNroPedido] = useState<string | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [openPopup, setOpenPopup] = useState(false);
  const [venta, setVenta] = useState<VentaResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>("");

  const isPagado = venta?.estadoId === 3;

  const MAX_FILES = 2; // Límite de archivos
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB por archivo

  useEffect(() => {
    // Decodificar el token
    if (!token) {
      setError("Token no proporcionado");
      setIsLoading(false);
      return;
    }

    const decoded = tokenHelper.decodeToken(token);
    if (!decoded) {
      setError("Token inválido o expirado");
      setIsLoading(false);
      return;
    }

    setEmail(decoded.email);
    setNroPedido(decoded.nroPedido);
  }, [token]);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const response: VentaResponse = await ventasService.getVenta(
          Number(nroPedido)
        );
        setVenta(response);
      } catch (error) {
        console.error("Error al obtener la venta:", error);
        setError("Error al cargar los datos del pedido");
      } finally {
        setIsLoading(false);
      }
    }

    if (nroPedido) {
      fetchData();
    }
  }, [nroPedido]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = Array.from(event.target.files);

      // Validar número máximo de archivos
      if (files.length + selectedFiles.length > MAX_FILES) {
        setError(`Máximo ${MAX_FILES} archivos permitidos`);
        return;
      }

      // Validar tamaño de archivos
      const invalidFiles = selectedFiles.filter(
        (file) => file.size > MAX_FILE_SIZE
      );
      if (invalidFiles.length > 0) {
        setError(`Algunos archivos exceden el tamaño máximo de 10MB`);
        return;
      }

      // Validar tipos de archivo
      const validTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      const invalidTypes = selectedFiles.filter(
        (file) => !validTypes.includes(file.type)
      );
      if (invalidTypes.length > 0) {
        setError("Solo se permiten archivos PDF, JPG, JPEG o PNG");
        return;
      }

      setFiles((prev) => [...prev, ...selectedFiles]);
      setError("");
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleUpload = async () => {
    if (!files) {
      setError("Por favor seleccione un archivo.");
      return;
    }

    if (!email || !nroPedido) {
      setError("Faltan datos del pedido.");
      return;
    }

    try {
      setIsUploading(true);
      setError("");

      const result = await ventasService.uploadComprobante(
        Number(nroPedido),
        files
      );

      if (result && result.success) {
        setOpenPopup(true);
        // Actualizar el estado de la venta localmente
        if (venta) {
          setVenta({ ...venta, estadoId: 3 });
        }
      } else {
        setError(result.message || "Error al cargar el comprobante");
      }
    } catch (err) {
      console.error(err);
      setError("Error inesperado al cargar el comprobante");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setFiles([]);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: "100%",
          padding: "3vw",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundImage: `url(${bannerFondo})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, textAlign: "center", maxWidth: 500 }}>
          <Title title={"Carga de comprobante de pago"} />

          <Stack spacing={2} mt={2}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: 15,
                  textAlign: "left",
                }}
              >
                Nombre de usuario:
              </Typography>
              <Typography
                sx={{ fontSize: 15, textAlign: "right", fontWeight: 500 }}
              >
                {email}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: 15,
                  textAlign: "left",
                }}
              >
                Número de Pedido:
              </Typography>
              <Typography
                sx={{ fontSize: 15, textAlign: "right", fontWeight: 500 }}
              >
                {nroPedido}
              </Typography>
            </Stack>

            <div className="d-flex justify-content-center">
              <hr style={{ width: "100%", marginTop: "0" }}></hr>
            </div>

            {error && (
              <Alert severity="error" onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {isPagado ? (
              <Stack direction="row" spacing={1} justifyContent="center">
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body1" fontWeight="bold">
                    El comprobante ya ha sido enviado.
                  </Typography>
                  <Typography variant="body2">
                    Se le informará por email el estado de su pedido.
                  </Typography>
                </Alert>
              </Stack>
            ) : (
              <>
                <div className="d-flex justify-content-between flex-wrap align-items-center ">
                  <Typography
                    sx={{
                      color: "black",
                      fontSize: 18,
                      marginTop: 4,
                      marginBottom: 2,
                    }}
                  >
                    Subí aquí tu comprobante de pago
                  </Typography>
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    sx={{
                      backgroundColor: colores.rojo,
                      padding: "1rem",
                    }}
                    startIcon={<CloudUploadIcon />}
                    disabled={isUploading}
                  >
                    Seleccionar archivo
                    <VisuallyHiddenInput
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                    />
                  </Button>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: colores.grisOscuro,
                      fontWeight: 200,
                      fontSize: 12,
                      marginTop: 2,
                      marginBottom: 2,
                    }}
                  >
                    Formatos admitidos: PDF, JPG, PNG (máximo 5MB)
                  </Typography>

                  {files.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 2, marginBottom: 2 }}>
                      <Box sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          mb={1}
                        >
                          <AttachFileIcon fontSize="small" />
                          <Typography variant="subtitle2">
                            Archivos seleccionados ({files.length})
                          </Typography>
                        </Stack>
                        <List dense>
                          {files.map((file, index) => (
                            <ListItem
                              key={index}
                              secondaryAction={
                                <IconButton
                                  edge="end"
                                  aria-label="delete"
                                  onClick={() => handleRemoveFile(index)}
                                  disabled={isUploading}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              }
                              sx={{
                                bgcolor: "background.default",
                                mb: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              <ListItemText
                                primary={file.name}
                                secondary={formatFileSize(file.size)}
                                primaryTypographyProps={{
                                  variant: "body2",
                                  noWrap: true,
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    </Paper>
                  )}

                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: colores.rojo,
                      padding: "1rem",
                    }}
                    onClick={handleUpload}
                    disabled={files.length === 0 || isUploading}
                    startIcon={
                      isUploading ? <CircularProgress size={20} /> : null
                    }
                  >
                    {isUploading ? "Cargando..." : "Cargar Comprobante"}
                  </Button>
                </div>

                <div className="d-flex justify-content-between flex-wrap align-items-center ">
                  <div className="d-flex justify-content-center">
                    <hr style={{ width: "100%", marginTop: "4vw" }}></hr>
                  </div>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LockIcon
                      sx={{
                        fontSize: "1.6rem",
                        color: colores.grisClaro,
                        marginLeft: 2,
                      }}
                    />
                    <Typography
                      sx={{
                        color: colores.grisClaro,
                        fontSize: 12,
                        margin: "2vw",
                        fontWeight: "bold",
                      }}
                    >
                      Este es un sitio seguro. Tu información será utilizada
                      únicamente para validar tu pago.
                    </Typography>
                  </Box>
                </div>
              </>
            )}
          </Stack>
        </Paper>
      </Box>

      <ConfirmacionPopup
        open={openPopup}
        onClose={handleClosePopup}
        titulo="¡Comprobante Cargado!"
        mensaje="Tu comprobante se ha cargado exitosamente."
      />
    </>
  );
}
